import React, { memo, useCallback } from "react";
import { Modal, Button } from "@repo/core-ui";
import {
  Globe,
  Activity,
  Settings,
  ShieldCheck,
  ShieldAlert,
  Calendar,
  Mail,
  FileText,
  ExternalLink,
} from "lucide-react";
import { ChannelListItem } from "@repo/types";
import moment from "moment";

interface ChannelDetailsModalProps {
  isOpen: boolean;
  onClose: () => void;
  channel: ChannelListItem | null;
}

export const ChannelDetailsModal: React.FC<ChannelDetailsModalProps> = memo(
  ({ isOpen, onClose, channel }) => {
    const renderDetailItem = useCallback(
      (label: string, value: any, icon?: React.ReactNode) => (
        <div className="flex flex-col gap-1 p-4 rounded-xl bg-slate-50 dark:bg-[#1b254b]/50 border border-slate-100 dark:border-white/10 transition-all hover:bg-white dark:hover:bg-[#1b254b] hover:border-slate-200 dark:hover:border-white/20">
          <div className="flex items-center gap-2">
            {icon && (
              <span className="text-slate-400 dark:text-slate-500">{icon}</span>
            )}
            <span className="text-xs font-semibold text-slate-500 dark:text-slate-500 uppercase tracking-wider">
              {label}
            </span>
          </div>
          <div className="text-sm font-bold text-slate-800 dark:text-slate-200 truncate">
            {value || (
              <span className="text-slate-400 dark:text-slate-600 italic font-medium">
                Not Provided
              </span>
            )}
          </div>
        </div>
      ),
      [],
    );

    return (
      <Modal
        open={isOpen}
        onClose={onClose}
        title="Channel Partner Insights"
        maxWidth="max-w-4xl"
        maxHeight="max-h-[95vh] sm:max-h-[90vh]"
        footer={
          <div className="flex justify-end w-full py-2">
            <Button
              onClick={onClose}
              className="bg-[#111c44] text-white hover:bg-black dark:hover:bg-[#1b254b] border-none px-8 rounded-xl font-bold text-sm shadow-lg shadow-black/20"
            >
              Close Overview
            </Button>
          </div>
        }
      >
        {channel ? (
          <div className="space-y-8 py-4 px-2">
            {/* Header Summary */}
            <div className="flex items-center justify-between p-6 rounded-3xl bg-slate-900 dark:bg-[#1b254b] text-white shadow-xl shadow-slate-900/10 relative overflow-hidden group">
              <div className="absolute top-0 right-0 p-8 opacity-5 dark:opacity-10 transform translate-x-4 -translate-y-4 group-hover:scale-110 transition-transform">
                <Globe size={120} />
              </div>
              <div className="relative z-10 flex items-center gap-6">
                <div className="w-16 h-16 rounded-2xl bg-white/10 backdrop-blur-md flex items-center justify-center font-bold text-2xl border border-white/10">
                  {(channel.ChannelCode || channel.ChannelName || "CH")
                    .substring(0, 2)
                    .toUpperCase()}
                </div>
                <div>
                  <h2 className="text-2xl font-bold tracking-tight mb-1">
                    {channel.ChannelName}
                  </h2>
                  <div className="flex items-center gap-3">
                    <span className="px-2.5 py-1 rounded-lg bg-blue-500/20 text-blue-100 dark:text-blue-300 font-semibold text-xs uppercase tracking-wider border border-blue-500/30 dark:border-blue-500/20">
                      {channel.ChannelCode || "N/A"}
                    </span>
                    <span
                      className={`px-2.5 py-1 rounded-lg font-semibold text-xs uppercase tracking-wider border ${
                        channel.IsActive
                          ? "bg-emerald-500/20 text-emerald-100 dark:text-emerald-300 border-emerald-500/30 dark:border-emerald-500/20"
                          : "bg-rose-500/20 text-rose-100 dark:text-rose-300 border-rose-500/30 dark:border-rose-500/20"
                      }`}
                    >
                      {channel.IsActive ? "ACTIVE" : "INACTIVE"}
                    </span>
                  </div>
                </div>
              </div>
              <div className="relative z-10 text-right hidden md:block">
                <div className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-1">
                  Priority Rank
                </div>
                <div className="text-3xl font-bold text-white">
                  P-{channel.Priority}
                </div>
              </div>
            </div>

            {/* Detailed Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {/* Section 1: Basic Identity */}
              <div className="space-y-4 lg:col-span-1">
                <div className="flex items-center gap-2 text-blue-600 dark:text-blue-400 px-1">
                  <Globe size={16} />
                  <span className="text-xs font-bold uppercase tracking-wider">
                    Identity
                  </span>
                </div>
                <div className="grid grid-cols-1 gap-3">
                  {renderDetailItem("Channel Type", channel.ChannelType)}
                  {renderDetailItem("Currency", channel.CurrencyCode)}
                  {renderDetailItem(
                    "Commission",
                    `${channel.CommissionPercentage}% (${channel.CommissionType})`,
                  )}
                </div>
              </div>

              {/* Section 2: Technical Configuration */}
              <div className="space-y-4 lg:col-span-2">
                <div className="flex items-center gap-2 text-slate-600 dark:text-slate-400 px-1">
                  <Settings size={16} />
                  <span className="text-xs font-bold uppercase tracking-wider">
                    Technical Pipeline
                  </span>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <div className="md:col-span-2">
                    {renderDetailItem(
                      "Base API URL",
                      channel.APIBaseURL,
                      <ExternalLink size={12} />,
                    )}
                  </div>
                  {renderDetailItem("API Version", channel.APIVersion)}
                  {renderDetailItem("Protocol", channel.Protocol)}
                  {renderDetailItem("Auth Strategy", channel.AuthType)}
                  <div className="flex flex-col gap-1 p-4 rounded-xl bg-slate-50 dark:bg-[#1b254b]/50 border border-slate-100 dark:border-white/10">
                    <span className="text-xs font-semibold text-slate-500 dark:text-slate-500 uppercase tracking-wider">
                      Security
                    </span>
                    <div
                      className={`flex items-center gap-2 text-xs font-bold ${
                        channel.IsCredentialsEncrypted
                          ? "text-emerald-600 dark:text-emerald-400"
                          : "text-amber-500 dark:text-amber-400"
                      }`}
                    >
                      {channel.IsCredentialsEncrypted ? (
                        <ShieldCheck size={16} />
                      ) : (
                        <ShieldAlert size={16} />
                      )}
                      {channel.IsCredentialsEncrypted
                        ? "ENCRYPTED STORAGE"
                        : "PLAIN TEXT EXPOSURE"}
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Section 3: Supported Features */}
            <div className="space-y-4">
              <div className="flex items-center gap-2 text-indigo-600 dark:text-indigo-400 px-1">
                <Activity size={16} />
                <span className="text-xs font-bold uppercase tracking-wider">
                  Feature Matrix
                </span>
              </div>
              <div className="flex flex-wrap gap-2">
                {(channel.SupportedFeatures || []).map((feature: any) => (
                  <div
                    key={feature}
                    className="px-4 py-2 rounded-lg bg-indigo-50 dark:bg-indigo-900/20 border border-indigo-100 dark:border-indigo-900/30 text-indigo-700 dark:text-indigo-300 text-xs font-bold uppercase tracking-wider shadow-sm"
                  >
                    {feature}
                  </div>
                ))}
                {(!channel.SupportedFeatures ||
                  channel.SupportedFeatures.length === 0) && (
                  <div className="text-xs font-medium text-slate-400 dark:text-slate-600 italic py-2">
                    No features defined
                  </div>
                )}
              </div>
            </div>

            {/* Section 4: External Links & Support */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="space-y-4">
                <div className="flex items-center gap-2 text-emerald-600 dark:text-emerald-400 px-1">
                  <Mail size={16} />
                  <span className="text-xs font-bold uppercase tracking-wider">
                    Support Access
                  </span>
                </div>
                {renderDetailItem(
                  "Technical Success Email",
                  channel.SupportEmail,
                  <Mail size={12} />,
                )}
              </div>
              <div className="space-y-4">
                <div className="flex items-center gap-2 text-orange-600 dark:text-orange-400 px-1">
                  <FileText size={16} />
                  <span className="text-xs font-bold uppercase tracking-wider">
                    Documentation
                  </span>
                </div>
                {renderDetailItem(
                  "API Docs Link",
                  channel.DocumentationURL,
                  <ExternalLink size={12} />,
                )}
              </div>
            </div>

            {/* Notes Section */}
            {channel.Notes && (
              <div className="p-6 rounded-2xl bg-blue-50 dark:bg-blue-900/10 border border-blue-100 dark:border-blue-900/20">
                <div className="flex items-center gap-2 text-blue-600 dark:text-blue-400 mb-3">
                  <FileText size={16} />
                  <span className="text-xs font-bold uppercase tracking-wider">
                    Internal Briefing
                  </span>
                </div>
                <p className="text-sm font-medium text-slate-600 dark:text-slate-400 leading-relaxed italic">
                  "{channel.Notes}"
                </p>
              </div>
            )}

            {/* Metadata */}
            <div className="flex items-center justify-between px-2 pt-4 border-t border-slate-100 dark:border-white/5 text-xs font-bold text-slate-400 dark:text-slate-600 uppercase tracking-wider">
              <div className="flex items-center gap-4">
                <div className="flex items-center gap-1.5 font-bold">
                  <Calendar size={10} />
                  Established:{" "}
                  {moment(channel.CreatedDate).format("DD MMM YYYY")}
                </div>
                <div className="flex items-center gap-1.5 font-bold">
                  <Calendar size={10} />
                  Last Audit:{" "}
                  {moment(channel.ModifiedDate).format("DD MMM YYYY")}
                </div>
              </div>
              <div className="flex items-center gap-1.5 font-bold text-slate-400">
                ID: REF-{channel.ChannelID.toString().padStart(6, "0")}
              </div>
            </div>
          </div>
        ) : null}
      </Modal>
    );
  },
);
ChannelDetailsModal.displayName = "ChannelDetailsModal";
